/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useEffect, useMemo } from 'react';
import { isWeb } from '@global/screens';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import ActionButton from '@components/navbar/ActionButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import * as Layout from '@components/screen/Layout';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetBranchListQuery,
  useGetBranchCountQuery,
  useGetBranchInfiniteListInfiniteQuery,
} from '@api/listApi';

const tableStructure = {
  name: {
    head: 'Branch Name',
    body: 'name',
    textStyle: { fontFamily: 'figtree-medium' },
  },
  branch_head: { head: 'Head Branch', body: 'branch_head.name' },
  regional: { head: 'Regional/Area', body: 'regional' },
  status: { head: 'Status', body: 'is_active', type: 'status_active' },
};

// Search-only list: the search box writes `name`; unwrap it into a $regex query.
const toApiFilter = (raw = {}) => {
  const filter = {};
  const name = raw.name?.$regex ?? raw.name;
  if (name) {
    filter.name = { $regex: name, $options: 'i' };
  }
  return filter;
};

const BranchListScreen = (props) => {
  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetBranchListQuery,
    countHook: useGetBranchCountQuery,
    infiniteHook: useGetBranchInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  const searchValue = useMemo(() => {
    const k = params.filter?.name;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  const rows = useMemo(() => {
    return isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
  }, [list.data]);

  // ── handlers ──
  const goToForm = () => {
    openChild('BranchListFormScreen');
  };

  const gotToDetail = (data) => {
    openChild('BranchListDetailScreen', {
      titleScreen: data.name,
      name: data.name,
      id: data.id,
    });
  };

  // ── effects ──
  // Own the header so the mobile Add goes through openChild (preserves the list
  // on return) rather than the navigator's plain navigate.
  useEffect(() => {
    props.navigation.setOptions({
      headerRight: () =>
        isWeb ? (
          <NotificationSettingButton {...props} />
        ) : (
          <ActionButton
            icon={require('@assets/img/icons/plus-white.svg')}
            onCallback={goToForm}
          />
        ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.navigation]);

  // ── render ──
  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Title title="Branch List">
            <Button
              type="primary"
              rounded={true}
              buttonStyle={{ width: 100 }}
              title={
                <SociollaText
                  type="medium"
                  style={{ fontSize: 14, color: Colors.white }}>
                  + Add
                </SociollaText>
              }
              onPress={() => goToForm()}
            />
          </Layout.Title>

          <Table
            filterName={scope}
            structures={tableStructure}
            data={rows}
            loading={list.isLoading}
            refetching={list.isFetching}
            searchKey="name"
            searchPlaceholder="Search by branch name..."
            hasCallback={true}
            hasNoHide={true}
            showFilter={false}
            onClickRow={({ data }) => gotToDetail(data)}
            totalData={count.data}
            skip={params.skip}
            limit={params.limit}
            searchValue={searchValue}
          />
        </>
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="name"
          placeholder="Search by branch name"
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          searchValue={searchValue}
          skip={params.skip}
          limit={params.limit}
          data={rows}
          totalData={count.data}
          renderItem={({ data, index }) => (
            <Layout.ItemMobile
              key={index}
              touchable={true}
              left={true}
              onPress={() => gotToDetail(data)}>
              <SociollaText type="medium">{data.name}</SociollaText>
            </Layout.ItemMobile>
          )}
        />
      )}
    </Layout.Container>
  );
};

export default BranchListScreen;
